/* ==========================================================================
   Avalanche — quick buy
   Any [data-quick-buy="<product url>"] trigger opens the global #quick-buy-modal filled with
   sections/quick-buy.liquid. Product features (gallery, variant picker…) come from product.js,
   which is injected once on first use.
   ========================================================================== */

const MODAL_ID = 'quick-buy-modal';
let assetsPromise = null;
let pending = null;

const getModal = () => document.getElementById(MODAL_ID);

function loadStyle(href) {
  if (!href) return Promise.resolve();
  const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find((l) => l.href.split('?')[0] === new URL(href, location.href).href.split('?')[0]);
  if (existing) return existing.sheet ? Promise.resolve() : new Promise((r) => { existing.addEventListener('load', r, { once: true }); setTimeout(r, 1500); });
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = resolve;
    link.onerror = resolve;
    document.head.append(link);
    setTimeout(resolve, 2000);
  });
}

function loadScript(src) {
  if (!src) return Promise.resolve();
  const base = new URL(src, location.href).pathname;
  const already = [...document.querySelectorAll('script[type="module"][src]')].some((s) => new URL(s.src, location.href).pathname === base);
  if (!already) {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    document.head.append(script);
  }
  return customElements.whenDefined('av-variant-picker');
}

function loadAssets(modal) {
  assetsPromise ??= Promise.all([
    loadStyle(modal.dataset.productStyle),
    // The picker definition is awaited with a timeout so a slow script never blocks the window
    Promise.race([loadScript(modal.dataset.productScript), new Promise((r) => setTimeout(r, 3000))])
  ]);
  return assetsPromise;
}

async function fetchQuickBuy(url, signal) {
  const target = new URL(url, location.origin);
  target.searchParams.set('section_id', 'quick-buy');
  const response = await fetch(target.toString(), { signal });
  if (!response.ok) throw new Error(String(response.status));
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  return doc.querySelector('[data-quick-buy-content]');
}

function setLoading(trigger, loading) {
  trigger?.classList.toggle('is-loading', loading);
  trigger?.setAttribute('aria-busy', String(loading));
}

export async function openQuickBuy(url, trigger) {
  const modal = getModal();
  if (!modal || !url) {
    if (url) window.location.href = url;
    return;
  }
  pending?.abort();
  const controller = (pending = new AbortController());
  setLoading(trigger, true);
  window.Theme?.loadingBar?.start();
  try {
    const [content] = await Promise.all([fetchQuickBuy(url, controller.signal), loadAssets(modal)]);
    if (controller.signal.aborted) return;
    if (!content) throw new Error('empty');
    const body = modal.querySelector('[data-quick-buy-body]');
    body.replaceChildren(document.importNode(content, true));
    body.scrollTop = 0;
    modal.open(trigger);
  } catch (error) {
    if (error.name !== 'AbortError') window.location.href = url;
  } finally {
    setLoading(trigger, false);
    window.Theme?.loadingBar?.done();
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-quick-buy]');
  if (!trigger) return;
  event.preventDefault();
  openQuickBuy(trigger.dataset.quickBuy, trigger);
});

/* Empty the window once closed so videos stop and stale forms disappear */
document.addEventListener('overlay:close', (event) => {
  if (event.target.id !== MODAL_ID) return;
  setTimeout(() => {
    const modal = getModal();
    if (modal && !modal.classList.contains('is-open')) modal.querySelector('[data-quick-buy-body]')?.replaceChildren();
  }, 350);
});

window.Theme = window.Theme || {};
window.Theme.openQuickBuy = openQuickBuy;
